import React from 'react';
import {
  Card,
  Container,
  ListGroup,
  Button,
  Alert,
  Spinner,
  Form,
  Row,
  Col,
} from 'react-bootstrap';
import PageHeading from '../PageHeading';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import axios from 'axios';
import { getOpenCartItemsThunk } from '../../store';
import CartItem from './CartItem';
import EmptyCart from './EmptyCart';
import AddressAutocomplete from './AddressAutocomplete';
import { PENDING_CHECKOUT_KEY, CHECKOUT_MESSAGE_TYPE } from './checkoutConstants';
import {
  stashPendingAddress,
} from '../../utils/pendingAddress';
import { Link } from 'react-router-dom';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
];

const emptyShipping = {
  fullName: '',
  email: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
};

const formatCents = (cents) => {
  if (cents == null || Number.isNaN(Number(cents))) return '—';
  return `$${(Number(cents) / 100).toFixed(2)}`;
};

const cartSubtotalDisplay = (arr) => {
  let subTotal = 0;
  arr.forEach((item) => {
    subTotal += item.quantity * item.price;
  });
  return subTotal;
};

const isShippingComplete = (shipping) =>
  Boolean(
    shipping.fullName &&
      shipping.email &&
      shipping.addressLine1 &&
      shipping.city &&
      shipping.state &&
      shipping.postalCode &&
      shipping.country
  );

class Cart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      checkingOut: false,
      awaitingPayment: false,
      checkoutError: null,
      abandonedCheckout: null,
      activeOrderId: null,
      collectingShipping: false,
      shipping: { ...emptyShipping },
      quote: null,
      quoting: false,
      quoteError: null,
      checkoutTotals: null,
      savedAddresses: [],
      selectedAddressId: null,
      saveAddressOnFile: true,
    };
    this.checkoutPopup = null;
    this.statusPollTimer = null;
    this.quoteTimer = null;
    this.quoteRequestId = 0;
    this.shippingSectionRef = null;
  }

  componentDidMount() {
    this.props.getCartItems();
    this.checkAbandonedCheckout();
    this.loadSavedAddresses();
    this.prefillEmailFromUser();
    window.addEventListener('pageshow', this.handlePageShow);
    window.addEventListener('message', this.handleCheckoutMessage);
    window.addEventListener('focus', this.handleWindowFocus);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.cartItems !== this.props.cartItems) {
      this.scheduleQuote();
    }
    if (!prevProps.isLoggedIn && this.props.isLoggedIn) {
      this.loadSavedAddresses();
      this.prefillEmailFromUser();
    }
    if (
      this.props.isLoggedIn &&
      this.props.userEmail &&
      prevProps.userEmail !== this.props.userEmail
    ) {
      this.prefillEmailFromUser();
    }
  }

  prefillEmailFromUser = () => {
    const email = this.props.userEmail;
    if (!email) return;
    this.setState((prev) => {
      if (prev.shipping.email) return null;
      return {
        shipping: { ...prev.shipping, email },
      };
    });
  };

  componentWillUnmount() {
    window.removeEventListener('pageshow', this.handlePageShow);
    window.removeEventListener('message', this.handleCheckoutMessage);
    window.removeEventListener('focus', this.handleWindowFocus);
    this.stopWatchingCheckout();
    if (this.quoteTimer) {
      clearTimeout(this.quoteTimer);
    }
  }

  stopWatchingCheckout = () => {
    if (this.statusPollTimer) {
      clearInterval(this.statusPollTimer);
      this.statusPollTimer = null;
    }
  };

  loadSavedAddresses = async () => {
    if (!this.props.isLoggedIn) {
      this.setState({ savedAddresses: [], selectedAddressId: null });
      return;
    }
    try {
      const { data } = await axios.get('/api/addresses');
      const addresses = data || [];
      const defaultAddr =
        addresses.find((a) => a.isDefault) || addresses[0] || null;
      this.setState(
        {
          savedAddresses: addresses,
          selectedAddressId: defaultAddr ? defaultAddr.id : null,
        },
        () => {
          if (defaultAddr) {
            this.applySavedAddress(defaultAddr);
          }
        }
      );
    } catch (err) {
      // Guest-only or transient — ignore
    }
  };

  applySavedAddress = (address) => {
    if (!address) return;
    this.setState(
      (prev) => ({
        shipping: {
          fullName: address.fullName || '',
          email: prev.shipping.email || '',
          addressLine1: address.addressLine1 || '',
          addressLine2: address.addressLine2 || '',
          city: address.city || address.locality || '',
          state: address.state || address.administrativeDistrictLevel1 || '',
          postalCode: address.postalCode || '',
          country: address.country || 'US',
        },
        selectedAddressId: address.id,
        quoteError: null,
        checkoutError: null,
      }),
      () => this.scheduleQuote()
    );
  };

  handleSelectSavedAddress = (evt) => {
    const value = evt.target.value;
    if (value === 'new') {
      this.setState((prev) => ({
        selectedAddressId: 'new',
        shipping: {
          ...emptyShipping,
          email: prev.shipping.email || '',
        },
        quote: null,
      }));
      return;
    }
    const id = parseInt(value, 10);
    const address = this.state.savedAddresses.find((a) => a.id === id);
    if (address) this.applySavedAddress(address);
  };

  handleCreateAccountFromShipping = () => {
    stashPendingAddress(this.state.shipping);
  };

  handlePageShow = (event) => {
    // Same-tab fallback: returning via browser back from Square.
    if (event.persisted && !this.state.awaitingPayment) {
      this.redirectAbandonedCheckoutToCancel();
    }
  };

  handleWindowFocus = () => {
    // When the buyer returns to this tab, re-check payment status.
    // Do NOT treat popup.closed as cancel — Square's COOP makes closed=true
    // while the Square window is still open (e.g. after clicking Continue).
    if (this.state.awaitingPayment && this.state.activeOrderId) {
      this.pollActiveOrder(this.state.activeOrderId);
    }
  };

  handleCheckoutMessage = (event) => {
    if (event.origin !== window.location.origin) return;
    const data = event.data;
    if (!data || data.type !== CHECKOUT_MESSAGE_TYPE || !data.orderId) return;
    this.finishCheckoutPaid(data.orderId);
  };

  finishCheckoutPaid = (orderId) => {
    this.stopWatchingCheckout();
    try {
      if (this.checkoutPopup && !this.checkoutPopup.closed) {
        this.checkoutPopup.close();
      }
    } catch (err) {
      // Cross-origin COOP may block access to the popup reference.
    }
    this.checkoutPopup = null;
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(PENDING_CHECKOUT_KEY);
    }
    this.setState({
      awaitingPayment: false,
      checkingOut: false,
      activeOrderId: null,
      abandonedCheckout: null,
      checkoutTotals: null,
      collectingShipping: false,
    });
    this.props.history.push(`/checkout/success?order=${orderId}`);
  };

  redirectAbandonedCheckoutToCancel = async () => {
    try {
      const res = await axios.get('/api/checkout/pending');
      if (res.data && res.data.pending && res.data.orderId) {
        this.props.history.push(
          `/checkout/cancel?order=${res.data.orderId}`
        );
        return;
      }
    } catch (err) {
      // fall through to local marker
    }
    if (typeof sessionStorage !== 'undefined') {
      const localId = sessionStorage.getItem(PENDING_CHECKOUT_KEY);
      if (localId) {
        this.props.history.push(`/checkout/cancel?order=${localId}`);
      }
    }
  };

  checkAbandonedCheckout = async () => {
    try {
      const res = await axios.get('/api/checkout/pending');
      if (res.data && res.data.pending && res.data.orderId) {
        this.setState({ abandonedCheckout: res.data });
      } else if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(PENDING_CHECKOUT_KEY);
        this.setState({ abandonedCheckout: null });
      }
    } catch (err) {
      // Non-blocking — cart still works without abandon detection.
    }
  };

  handleShippingChange = (event) => {
    const { name, value } = event.target;
    this.setState(
      (prev) => ({
        shipping: { ...prev.shipping, [name]: value },
        selectedAddressId:
          prev.selectedAddressId && prev.selectedAddressId !== 'new'
            ? 'new'
            : prev.selectedAddressId,
        quoteError: null,
        checkoutError: null,
      }),
      () => this.scheduleQuote()
    );
  };

  handleAddressLine1Change = (value) => {
    this.setState(
      (prev) => ({
        shipping: { ...prev.shipping, addressLine1: value },
        selectedAddressId:
          prev.selectedAddressId && prev.selectedAddressId !== 'new'
            ? 'new'
            : prev.selectedAddressId,
        quoteError: null,
        checkoutError: null,
      }),
      () => this.scheduleQuote()
    );
  };

  /**
   * Apply structured place details. Never overwrite an existing apartment/unit
   * the customer already typed; only fill addressLine2 from Google if empty.
   */
  handleSelectAddress = (address) => {
    this.setState(
      (prev) => {
        const existingLine2 = (prev.shipping.addressLine2 || '').trim();
        const suggestedLine2 = (address.suggestedAddressLine2 || '').trim();
        return {
          shipping: {
            ...prev.shipping,
            addressLine1: address.addressLine1 || prev.shipping.addressLine1,
            addressLine2: existingLine2 || suggestedLine2 || '',
            city: address.city || '',
            state: address.state || '',
            postalCode: address.postalCode || '',
            country: address.country || 'US',
          },
          selectedAddressId:
            prev.selectedAddressId && prev.selectedAddressId !== 'new'
              ? 'new'
              : prev.selectedAddressId,
          quoteError: null,
          checkoutError: null,
        };
      },
      () => this.scheduleQuote()
    );
  };

  scheduleQuote = () => {
    if (this.quoteTimer) {
      clearTimeout(this.quoteTimer);
    }
    if (!this.state.collectingShipping || !isShippingComplete(this.state.shipping)) {
      this.setState({ quote: null, quoting: false, quoteError: null });
      return;
    }
    this.quoteTimer = setTimeout(() => {
      this.fetchQuote();
    }, 400);
  };

  fetchQuote = async () => {
    if (!isShippingComplete(this.state.shipping)) return;
    const requestId = ++this.quoteRequestId;
    this.setState({ quoting: true, quoteError: null });
    try {
      const res = await axios.post('/api/checkout/quote', {
        shippingAddress: this.state.shipping,
      });
      if (requestId !== this.quoteRequestId) return;
      this.setState({
        quote: res.data,
        quoting: false,
        quoteError: null,
      });
    } catch (err) {
      if (requestId !== this.quoteRequestId) return;
      const message =
        (err.response && err.response.data && err.response.data.error) ||
        err.message ||
        'Unable to calculate shipping and tax.';
      this.setState({
        quoting: false,
        quote: null,
        quoteError: message,
      });
    }
  };

  openSquareCheckout = (checkoutUrl, orderId) => {
    if (orderId && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(PENDING_CHECKOUT_KEY, orderId);
    }

    // Must open from the click handler stack; popup blockers apply otherwise.
    const popup = window.open(
      checkoutUrl,
      'naturesnefer_square_checkout',
      'width=480,height=800'
    );

    if (!popup) {
      // Popup blocked — fall back to same-tab redirect.
      window.location.href = checkoutUrl;
      return;
    }

    this.checkoutPopup = popup;
    this.setState({
      awaitingPayment: true,
      checkingOut: false,
      activeOrderId: orderId,
      abandonedCheckout: null,
      checkoutError: null,
    });

    // Poll for paid status only. Do not watch popup.closed — Square sets
    // Cross-Origin-Opener-Policy, so browsers report closed=true during
    // normal checkout navigation (e.g. Continue), which caused false cancels.
    this.statusPollTimer = setInterval(() => {
      this.pollActiveOrder(orderId);
    }, 2000);
  };

  pollActiveOrder = async (orderId) => {
    if (!orderId) return;
    try {
      const res = await axios.get(`/api/checkout/${orderId}`, {
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        params: { _: Date.now() },
      });
      if (res.data && res.data.status === 'paid') {
        this.finishCheckoutPaid(orderId);
      }
    } catch (err) {
      // keep waiting
    }
  };

  handleResumeCheckout = () => {
    const { abandonedCheckout } = this.state;
    if (!abandonedCheckout || !abandonedCheckout.checkoutUrl) return;
    this.setState({
      checkoutTotals: {
        subtotalCents: abandonedCheckout.subtotalCents,
        shippingCents: abandonedCheckout.shippingCents,
        taxCents: abandonedCheckout.taxCents,
        totalCents: abandonedCheckout.totalCents,
      },
    });
    this.openSquareCheckout(
      abandonedCheckout.checkoutUrl,
      abandonedCheckout.orderId
    );
  };

  handleCancelCheckout = () => {
    const orderId =
      (this.state.abandonedCheckout && this.state.abandonedCheckout.orderId) ||
      this.state.activeOrderId;
    if (!orderId) return;
    this.stopWatchingCheckout();
    try {
      if (this.checkoutPopup && !this.checkoutPopup.closed) {
        this.checkoutPopup.close();
      }
    } catch (err) {
      // ignore COOP access errors
    }
    this.checkoutPopup = null;
    this.setState({ awaitingPayment: false, activeOrderId: null });
    this.props.history.push(`/checkout/cancel?order=${orderId}`);
  };

  handleCheckout = async () => {
    if (this.state.checkingOut || this.state.awaitingPayment) return;

    // First click: reveal shipping address form (do not open Square yet).
    if (!this.state.collectingShipping) {
      this.setState(
        {
          collectingShipping: true,
          checkoutError: null,
        },
        () => {
          if (this.shippingSectionRef && this.shippingSectionRef.scrollIntoView) {
            this.shippingSectionRef.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }
      );
      return;
    }

    if (!isShippingComplete(this.state.shipping)) {
      this.setState({
        checkoutError: 'Please complete the shipping address before checkout.',
      });
      return;
    }
    this.setState({ checkingOut: true, checkoutError: null });
    try {
      if (this.props.isLoggedIn) {
        const usingSaved =
          this.state.selectedAddressId &&
          this.state.selectedAddressId !== 'new';
        if (!usingSaved && this.state.saveAddressOnFile) {
          try {
            await axios.post('/api/addresses', {
              ...this.state.shipping,
              isDefault: this.state.savedAddresses.length === 0,
            });
            await this.loadSavedAddresses();
          } catch (saveErr) {
            console.error('Could not save address on file:', saveErr.message);
          }
        }
      } else if (isShippingComplete(this.state.shipping)) {
        // Keep available for signup after payment if they choose to create an account.
        stashPendingAddress(this.state.shipping);
      }

      const res = await axios.post('/api/checkout/square', {
        shippingAddress: this.state.shipping,
      });
      const checkoutUrl = res.data && res.data.checkoutUrl;
      const orderId = res.data && res.data.orderId;
      if (!checkoutUrl || !orderId) {
        throw new Error('Checkout URL missing from response.');
      }
      this.setState({
        checkoutTotals: {
          subtotalCents: res.data.subtotalCents,
          shippingCents: res.data.shippingCents,
          taxCents: res.data.taxCents,
          totalCents: res.data.totalCents,
          shippingName: res.data.shippingName,
        },
        quote: {
          subtotalCents: res.data.subtotalCents,
          shippingCents: res.data.shippingCents,
          taxCents: res.data.taxCents,
          totalCents: res.data.totalCents,
          shippingName: res.data.shippingName,
          taxPreviewAvailable: true,
        },
      });
      this.openSquareCheckout(checkoutUrl, orderId);
    } catch (err) {
      const message =
        (err.response && err.response.data && err.response.data.error) ||
        err.message ||
        'Unable to start checkout. Please try again.';
      this.setState({ checkingOut: false, checkoutError: message });
    }
  };

  handleCancelShippingStep = () => {
    this.setState({
      collectingShipping: false,
      checkoutError: null,
      quoteError: null,
      quote: null,
    });
  };

  renderOrderSummary(totals, { pendingTax = false } = {}) {
    if (!totals) return null;
    return (
      <div className="cart-order-summary" aria-live="polite">
        <div className="cart-order-summary__row">
          <span>Subtotal</span>
          <span>{formatCents(totals.subtotalCents)}</span>
        </div>
        <div className="cart-order-summary__row">
          <span>{totals.shippingName || 'Shipping'}</span>
          <span>{formatCents(totals.shippingCents)}</span>
        </div>
        <div className="cart-order-summary__row">
          <span>Sales Tax</span>
          <span>
            {pendingTax && !(totals.taxCents > 0)
              ? 'Calculated at checkout'
              : formatCents(totals.taxCents)}
          </span>
        </div>
        <div className="cart-order-summary__row cart-order-summary__row--total">
          <span>Total</span>
          <span>
            {pendingTax && !(totals.taxCents > 0)
              ? 'Finalized at checkout'
              : formatCents(totals.totalCents)}
          </span>
        </div>
      </div>
    );
  }

  renderAwaitingPayment() {
    const totals = this.state.checkoutTotals || this.state.quote;
    return (
      <Container className="pad-t R-clm cart-page">
        <PageHeading as="h2" icon="card">
          Complete payment
        </PageHeading>
        <Card border="light" className="Form pad2">
          <div className="text-center">
            <Spinner animation="border" role="status" className="mb-3" />
            <p>
              Finish checkout in the Square window. This page will update when
              payment succeeds.
            </p>
            {this.renderOrderSummary(totals)}
            <p className="text-muted small">
              If you close the Square window without paying, click Cancel
              checkout below.
            </p>
            <Button
              variant="outline-secondary"
              onClick={this.handleCancelCheckout}
            >
              Cancel checkout
            </Button>
          </div>
        </Card>
      </Container>
    );
  }

  renderShippingForm() {
    const { shipping, savedAddresses, selectedAddressId, saveAddressOnFile } =
      this.state;
    const { isLoggedIn } = this.props;
    const shippingReady = isShippingComplete(shipping);
    const usingSaved =
      selectedAddressId && selectedAddressId !== 'new';

    return (
      <div
        ref={(el) => {
          this.shippingSectionRef = el;
        }}
      >
        <Card border="light" className="cart-panel pad2 cart-shipping">
          <Card.Title>Shipping address</Card.Title>
          {isLoggedIn && savedAddresses.length > 0 && (
            <Form.Group className="mb-3">
              <Form.Label>Address on file</Form.Label>
              <Form.Control
                as="select"
                value={selectedAddressId || 'new'}
                onChange={this.handleSelectSavedAddress}
              >
                {savedAddresses.map((addr) => (
                  <option key={addr.id} value={addr.id}>
                    {(addr.label ? `${addr.label} — ` : '') +
                      `${addr.addressLine1}, ${addr.city || addr.locality}`}
                    {addr.isDefault ? ' (default)' : ''}
                  </option>
                ))}
                <option value="new">Use a new address</option>
              </Form.Control>
            </Form.Group>
          )}
          <Form>
            <Form.Group className="mb-2">
              <Form.Label>Full name</Form.Label>
              <Form.Control
                name="fullName"
                value={shipping.fullName}
                onChange={this.handleShippingChange}
                autoComplete="name"
                required
                autoFocus
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Email (for receipt)</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={shipping.email}
                onChange={this.handleShippingChange}
                autoComplete="email"
                required
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <AddressAutocomplete
                value={shipping.addressLine1}
                onChange={this.handleAddressLine1Change}
                onSelectAddress={this.handleSelectAddress}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Apartment, suite, etc. (optional)</Form.Label>
              <Form.Control
                name="addressLine2"
                value={shipping.addressLine2}
                onChange={this.handleShippingChange}
                autoComplete="address-line2"
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>City</Form.Label>
              <Form.Control
                name="city"
                value={shipping.city}
                onChange={this.handleShippingChange}
                autoComplete="address-level2"
                required
              />
            </Form.Group>
            <Row>
              <Col xs={6}>
                <Form.Group className="mb-2">
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    as="select"
                    name="state"
                    value={shipping.state}
                    onChange={this.handleShippingChange}
                    autoComplete="address-level1"
                    required
                  >
                    <option value="">Select</option>
                    {US_STATES.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group className="mb-2">
                  <Form.Label>ZIP</Form.Label>
                  <Form.Control
                    name="postalCode"
                    value={shipping.postalCode}
                    onChange={this.handleShippingChange}
                    autoComplete="postal-code"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-2">
              <Form.Label>Country</Form.Label>
              <Form.Control
                as="select"
                name="country"
                value={shipping.country}
                onChange={this.handleShippingChange}
                autoComplete="country"
                required
              >
                <option value="US">United States</option>
              </Form.Control>
            </Form.Group>
            {isLoggedIn && !usingSaved && (
              <Form.Group className="mb-0">
                <Form.Check
                  type="checkbox"
                  id="save-address-on-file"
                  label="Save this address on file for next time"
                  checked={saveAddressOnFile}
                  onChange={(evt) =>
                    this.setState({ saveAddressOnFile: evt.target.checked })
                  }
                />
              </Form.Group>
            )}
          </Form>
          {!isLoggedIn && shippingReady && (
            <div className="cart-create-account-prompt" role="note">
              <p>
                Create an account to save this address on file and see past
                orders.
              </p>
              <Button
                as={Link}
                to="/signup"
                variant="outline-success"
                size="sm"
                onClick={this.handleCreateAccountFromShipping}
              >
                Create account
              </Button>
            </div>
          )}
        </Card>
      </div>
    );
  }

  render() {
    const cartItems = this.props.cartItems;
    const displaySubtotal = cartSubtotalDisplay(cartItems);
    const {
      checkingOut,
      checkoutError,
      abandonedCheckout,
      awaitingPayment,
      quote,
      quoting,
      quoteError,
      shipping,
      collectingShipping,
    } = this.state;

    if (awaitingPayment) {
      return this.renderAwaitingPayment();
    }

    if (!cartItems || !cartItems.length) {
      return (
        <>
          {abandonedCheckout && (
            <Container className="pad-t">
              <Alert variant="warning">
                You have an unfinished Square checkout.{' '}
                <Button
                  variant="link"
                  className="p-0 align-baseline"
                  onClick={this.handleCancelCheckout}
                >
                  Cancel it
                </Button>
                {abandonedCheckout.checkoutUrl && (
                  <>
                    {' '}
                    or{' '}
                    <Button
                      variant="link"
                      className="p-0 align-baseline"
                      onClick={this.handleResumeCheckout}
                    >
                      resume payment
                    </Button>
                  </>
                )}
                .
              </Alert>
            </Container>
          )}
          <EmptyCart />
        </>
      );
    }

    const summaryTotals = quote || {
      subtotalCents: Math.round(displaySubtotal * 100),
      shippingCents: null,
      taxCents: null,
      totalCents: null,
    };
    const shippingReady = isShippingComplete(shipping);
    const canPay =
      collectingShipping && shippingReady && !quoting && !quoteError;

    return (
      <Container
        fluid
        className={
          collectingShipping
            ? 'pad-t cart-page cart-page--with-shipping'
            : 'pad-t cart-page'
        }
      >
        <div className="cart-page-inner">
        <div className="cart-page-header">
          <PageHeading as="h2" icon="bag">
            Shopping Bag
          </PageHeading>
          <div className="secure-payment-badge" role="note">
            <svg
              className="secure-payment-badge__icon"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              aria-hidden="true"
              focusable="false"
            >
              <path
                fill="currentColor"
                d="M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5zm-3 5a3 3 0 0 1 6 0v3H9V6zm3 8a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3z"
              />
            </svg>
            <span>Secure payment by Square</span>
          </div>
        </div>
        {abandonedCheckout && (
          <Alert variant="warning">
            Your previous Square checkout was not completed.{' '}
            <Button
              variant="outline-secondary"
              size="sm"
              className="me-2"
              onClick={this.handleCancelCheckout}
            >
              Cancel checkout
            </Button>
            {abandonedCheckout.checkoutUrl && (
              <Button
                variant="outline-success"
                size="sm"
                onClick={this.handleResumeCheckout}
              >
                Resume payment
              </Button>
            )}
          </Alert>
        )}
        <div
          className={
            collectingShipping
              ? 'cart-main-row cart-main-row--with-shipping'
              : 'cart-main-row'
          }
        >
          <div className="cart-bag-col">
            <Card border="light" className="cart-panel cart-bag-card">
              <div className="Nav-right spc-btwn pad2">
                <Card.Title>Product</Card.Title>
                <Card.Title>Price</Card.Title>
              </div>
              <ListGroup variant="flush">
                {cartItems.map((item) => {
                  return (
                    <CartItem
                      key={item.id || item.productId || item.name}
                      cartItem={item}
                    />
                  );
                })}
              </ListGroup>
            </Card>
          </div>
          {collectingShipping && (
            <div className="cart-shipping-col">{this.renderShippingForm()}</div>
          )}
        </div>

        <Card className="cart-panel L-clm pad2 cart-summary-card">
          {collectingShipping && quoting && (
            <div className="text-muted small mb-2">
              <Spinner animation="border" size="sm" className="me-2" />
              Calculating shipping and tax…
            </div>
          )}
          {collectingShipping && quoteError && (
            <Alert variant="danger">{quoteError}</Alert>
          )}
          {!collectingShipping && (
            <Alert variant="info" className="mb-2">
              Shipping and tax are calculated during checkout.
            </Alert>
          )}
          {collectingShipping
            ? this.renderOrderSummary(summaryTotals, {
                pendingTax:
                  shippingReady && quote && !quote.taxPreviewAvailable,
              })
            : (
              <div className="cart-order-summary" aria-live="polite">
                <div className="cart-order-summary__row cart-order-summary__row--total">
                  <span>Subtotal</span>
                  <span>${Number(displaySubtotal).toFixed(2)}</span>
                </div>
              </div>
            )}
          {checkoutError && <Alert variant="danger">{checkoutError}</Alert>}
          <div className="cart-checkout-actions">
            {collectingShipping && (
              <Button
                variant="outline-secondary"
                className="me-2"
                onClick={this.handleCancelShippingStep}
                disabled={checkingOut}
              >
                Back
              </Button>
            )}
            <Button
              variant="success"
              onClick={this.handleCheckout}
              disabled={
                checkingOut || (collectingShipping && !canPay)
              }
            >
              {checkingOut ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Opening Square…
                </>
              ) : collectingShipping ? (
                'Continue to payment'
              ) : (
                'Checkout'
              )}
            </Button>
          </div>
        </Card>
        </div>
      </Container>
    );
  }
}

/**
 * CONTAINER
 */
const mapState = (state) => {
  return {
    cartItems: state.cartItems,
    isLoggedIn: !!state.user.id,
    userEmail: state.user.email || '',
  };
};

const mapDispatch = (dispatch) => {
  return {
    getCartItems: () => dispatch(getOpenCartItemsThunk()),
  };
};

export default connect(mapState, mapDispatch)(Cart);

/**
 * PROP TYPES
 */
Cart.propTypes = {
  cartItems: PropTypes.object,
  isLoggedIn: PropTypes.bool,
  userEmail: PropTypes.string,
  getCartItems: PropTypes.func,
};
